import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getKeysFromSessionStorage, readFromSessionStorage, saveToSessionStorage } from 'framework-listing-lib/utils/sessionStorage';
import { safeParseJSON } from 'framework-listing-lib/utils/json';
import { useCurrentViewId, useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useObjectTypeId from './useObjectTypeId';

/**
 * Stores the current filterState used in the lib in sessionStorage so changes in views (such as filters)
 * are temporarily saved.
 * The main use-case for this is:
 *
 * 1. User opens View A and applies 1 or more quick filters or filter groups
 * 2. User opens View B
 * 3. User opens View A ➡️ changes from step 1 are still taken into account
 */
export default function useSyncFiltersWithSessionStorage(filters) {
  "use memo";

  const $ = _c(15);
  const [shouldSyncWithSessionStorage, setShouldSyncWithSessionStorage] = useState(false);
  const currentViewId = useCurrentViewId();
  const objectTypeId = useObjectTypeId();
  const namespace = useViewsNamespace();
  const sessionStorageKey = currentViewId ? `ListingLibFilterState:${objectTypeId}:${currentViewId}:${namespace}` : undefined;
  let t0;
  let t1;
  if ($[0] !== filters.filterGroups || $[1] !== filters.quickFilters || $[2] !== sessionStorageKey || $[3] !== shouldSyncWithSessionStorage) {
    t0 = () => {
      if (!sessionStorageKey || !shouldSyncWithSessionStorage) {
        return;
      }
      saveToSessionStorage(sessionStorageKey, JSON.stringify({
        filterGroups: filters.filterGroups || [{
          filters: []
        }],
        quickFilters: filters.quickFilters || []
      }));
    };
    t1 = [filters.filterGroups, filters.quickFilters, sessionStorageKey, shouldSyncWithSessionStorage];
    $[0] = filters.filterGroups;
    $[1] = filters.quickFilters;
    $[2] = sessionStorageKey;
    $[3] = shouldSyncWithSessionStorage;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[6] !== shouldSyncWithSessionStorage) {
    t2 = () => {
      if (!shouldSyncWithSessionStorage) {
        getKeysFromSessionStorage().filter(_temp).forEach(_temp2);
      }
    };
    t3 = [shouldSyncWithSessionStorage];
    $[6] = shouldSyncWithSessionStorage;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setShouldSyncWithSessionStorage(true);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const turnOnSessionStorage = t4;
  let t5;
  if ($[10] !== sessionStorageKey || $[11] !== shouldSyncWithSessionStorage) {
    t5 = shouldSyncWithSessionStorage && sessionStorageKey ? safeParseJSON(readFromSessionStorage(sessionStorageKey), undefined) : undefined;
    $[10] = sessionStorageKey;
    $[11] = shouldSyncWithSessionStorage;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = {
      filterStateFromSessionStorage: t5,
      turnOnSessionStorage
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp2(key_0) {
  sessionStorage.removeItem(key_0);
}
function _temp(key) {
  return key.startsWith("ListingLibFilterState");
}